import { useTranslation } from 'react-i18next';
import { useState } from 'react';

import { Quota } from '../../types/accommodation';
import Button from '../../components/Button';
import { releaseQuota } from '../../utils/functions';
import ModalWindow from '../../components/ModalWindow';
import Spinner from '../../components/Spinner';
import { Config } from '../../types/drupal';
import { QuotaInfo } from '../../types/accommodation';

interface Props {
  info: Quota;
  email: string;
  index?: number;
  language: string;
  config: Config | null;
  quotaInfo: QuotaInfo;
  idToken: string;
}

const QuotaTable: React.FC<Props> = (props: Props): JSX.Element => {
  const { t } = useTranslation(['quotas']);
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [quotaReleased, setQuotaReleased] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>();
  const [isLoading, setIsLoading] = useState(false);

  const deadLineText = props.config?.field_quota_deadline_text;

  const dateFormatOptions: Intl.DateTimeFormatOptions = {
    year: 'numeric',
    month: 'numeric',
    day: 'numeric',
  };

  const endDate = new Date(props.info.end);
  const endDateFormatted = endDate.toLocaleDateString(
    'fi-FI',
    dateFormatOptions
  );

  const startDate = new Date(endDate.getTime());
  // If the season ends in April, set the start date to November,
  // and vice versa.
  if (endDate.getMonth() === 3) {
    startDate.setMonth(10, 1);
    startDate.setFullYear(endDate.getFullYear() - 1, 10, 1);
  } else {
    startDate.setMonth(4, 1);
  }

  const datesFormatted = ` ${startDate.toLocaleDateString(
    'fi-FI',
    dateFormatOptions
  )} - ${endDateFormatted}`;

  const month = startDate.getMonth() + 1;
  const season =
    month >= 11 || month < 5
      ? `${t('Winter season')} ${startDate.getFullYear()} - ${
          startDate.getFullYear() + 1
        }`
      : `${t('Summer season')} ${startDate.getFullYear()}`;

  const listItemClasses = 'px-4 py-2 flex justify-between';

  async function release() {
    setIsLoading(true);
    const response = await releaseQuota(
      props.email,
      season,
      props.language,
      props.quotaInfo.title,
      props.idToken
    );
    if (response) {
      setQuotaReleased(true);
    } else {
      setErrorMessage(t('Error releasing quota.'));
    }
    setIsLoading(false);
  }

  const elementClassName = `season-${props.index ?? 'active'}`;

  return (
    <div
      className={`border-1 border-black rounded-md pt-4 pb-2 my-3 bg-white ${elementClassName}`}
    >
      <p className="font-bold text-lg uppercase px-4">{season}</p>
      <p className="px-4 py-2">{datesFormatted}</p>
      <ul>
        <li className={`${listItemClasses} bg-gray-200`}>
          <span className="font-bold">{t('Available days')}</span>{' '}
          <span>{49 * props.quotaInfo.roomIds.length}</span>
        </li>
        <li className={listItemClasses}>
          <span className="font-bold">{t('Remaining amount')}</span>{' '}
          <span>{props.info.quota}</span>
        </li>
      </ul>

      <div className="px-4 flex justify-center flex-col">
        <hr className="border-gray-500 my-8 border-1" />

        <Button onClick={() => setModalIsOpen(true)}>
          {t('Release quota') ?? 'Release quota'}
        </Button>
        <p
          className="my-4"
          dangerouslySetInnerHTML={{
            __html: deadLineText?.replace('%date', endDateFormatted) ?? '',
          }}
        ></p>
      </div>

      <ModalWindow
        isOpen={modalIsOpen}
        setIsOpen={setModalIsOpen}
        title={quotaReleased ? t('Quota released!') : t('Release quota')}
      >
        <div className="py-4">
          <p className="uppercase font-bold">{season}</p>
          <p>{datesFormatted}</p>

          {/* User hasn't interacted */}
          {!quotaReleased && !errorMessage && !isLoading && (
            <>
              {props.config && (
                <p
                  className="my-4"
                  dangerouslySetInnerHTML={{
                    __html: props.config.field_quota_release_text,
                  }}
                ></p>
              )}

              <Button onClick={release} className="w-full md:w-fit md:mr-6">
                {t('Confirm', { ns: 'common' })}
              </Button>
              <Button
                className="mt-4 w-full md:w-fit"
                variant="transparent"
                onClick={() => setModalIsOpen(false)}
              >
                {t('Cancel', { ns: 'common' })}
              </Button>
            </>
          )}

          {isLoading && <Spinner />}

          {/* Quota was succesfully released */}
          {quotaReleased && (
            <div>
              <div className="py-6">
                {props.config && (
                  <p
                    dangerouslySetInnerHTML={{
                      __html: props.config.field_quota_is_released_text,
                    }}
                  ></p>
                )}
              </div>

              <Button onClick={() => setModalIsOpen(false)}>
                {t('Close the window', { ns: 'common' })}
              </Button>
            </div>
          )}
          {errorMessage && <p>{errorMessage}</p>}
        </div>
      </ModalWindow>
    </div>
  );
};

export default QuotaTable;
